import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { CoachingVisibility, Player } from "@/lib/types";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { TkticMultiSelect } from "@/components/ui/TkticMultiSelect";
import {
  NOTE_CATEGORIES,
  NOTE_CATEGORY_LABELS,
} from "./Composer";
import { PlusIcon } from "./icons";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";

/**
 * Clip composer — the "Save Clip" form in Coach Review.
 *
 * A clip is a `[start_seconds, end_seconds]` window of a match's
 * video. Ports the legacy Replay clip composer
 * (`js/coaching/clips.js` + the `coach-clip-form-template`):
 *
 *   - Full field set: title, visibility, category, start, end,
 *     live duration label (red when the window is invalid), tagged
 *     players, description.
 *   - **Match + slot are NOT editable here.** A clip's match
 *     anchor is immutable — `UpdateCoachingClipRequest` is
 *     `extra="forbid"` and omits `match_id`/`slot`. The parent's
 *     `MatchSlotPicker` owns the match/slot selection; the clip
 *     composer just shows them read-only. On EDIT the parent locks
 *     the picker entirely.
 *   - `source_note_id` shows as a read-only "From note #N" pill
 *     when present. It's CREATE-only — never editable, never
 *     re-emitted on PATCH (rebinding the source would silently
 *     swap which note's drawing snapshot the clip captured).
 *   - Client-side window validation: `end > start` and
 *     `duration ≤ 120s` (`MAX_CLIP_DURATION_SECONDS`, mirrors the
 *     backend `_MAX_CLIP_DURATION_SECONDS`) — block submit before
 *     the backend 422s.
 *
 * Like `Composer`, exposes a `ClipComposerHandle` so the parent
 * can read live values (focus-mode snapshot continuity) and reset
 * after a save.
 */

/** Mirrors the backend `_MAX_CLIP_DURATION_SECONDS` in `models.py`. */
export const MAX_CLIP_DURATION_SECONDS = 120;

const VISIBILITY_OPTIONS: ReadonlyArray<{
  value: CoachingVisibility;
  label: string;
  description: string;
}> = [
  { value: "private", label: "Coach-only (private)", description: "Only coaches see this." },
  { value: "team", label: "Team", description: "All team members + linked viewers." },
  { value: "player", label: "Player + family", description: "Only tagged players + their family accounts." },
  { value: "unlisted", label: "Unlisted", description: "Direct link only — not in the team feed." },
];

export const clipComposerSchema = z
  .object({
    title: z.string().min(1, "Title is required").max(200),
    description: z.string().max(4000),
    category: z.enum(NOTE_CATEGORIES),
    visibility: z.enum(["private", "team", "player", "unlisted"]),
    // Seconds. The number `<input>`s register with
    // `valueAsNumber: true` so RHF hands the resolver real numbers
    // — `z.coerce.number()` here would make the schema's INPUT type
    // `unknown`, which trips RHF's resolver generics (same gotcha
    // the note Composer's schema docstring calls out).
    start_seconds: z.number().min(0, "Start must be ≥ 0"),
    end_seconds: z.number().min(0, "End must be ≥ 0"),
    player_ids: z.array(z.string()),
  })
  .refine((v) => v.end_seconds > v.start_seconds, {
    message: "End must be after start",
    path: ["end_seconds"],
  })
  .refine(
    (v) => v.end_seconds - v.start_seconds <= MAX_CLIP_DURATION_SECONDS,
    {
      message: `Clip must be ${MAX_CLIP_DURATION_SECONDS}s or shorter`,
      path: ["end_seconds"],
    },
  );

export type ClipComposerValues = z.infer<typeof clipComposerSchema>;

export type ClipComposerHandle = {
  getValues: () => ClipComposerValues;
  isDirty: () => boolean;
  reset: (next?: Partial<ClipComposerValues>) => void;
};

export type ClipComposerProps = {
  defaultValues?: Partial<ClipComposerValues>;
  players: Player[];
  onSubmit: SubmitHandler<ClipComposerValues>;
  submitLabel?: string;
  /** Match label shown read-only above the window inputs. The
   * picker that drives it lives in the parent. */
  matchLabel: string;
  /** Slot shown read-only beside the match. */
  slot: string;
  /** When true, the clip is being edited — the match/slot are
   * fixed (the parent also locks its picker). Purely a hint for
   * the read-only banner copy; the composer never emits
   * match_id/slot regardless. */
  isEditing?: boolean;
  /** Source note id, when the clip was derived from a timeline
   * chip / co-located note. Read-only — shown as a "From note #N"
   * pill. */
  sourceNoteId?: number | null;
  /** A4: frequency-ranked player ids (≤5, in roster) rendered as
   * quick-add chips above the "Tag players" picker. */
  suggestedPlayerIds?: string[];
};

const DEFAULTS: ClipComposerValues = {
  title: "",
  description: "",
  category: "other",
  visibility: "team",
  start_seconds: 0,
  end_seconds: 0,
  player_ids: [],
};

export const ClipComposer = forwardRef<ClipComposerHandle, ClipComposerProps>(
  function ClipComposer(
    {
      defaultValues,
      players,
      onSubmit,
      submitLabel = "Save clip",
      matchLabel,
      slot,
      isEditing = false,
      sourceNoteId,
      suggestedPlayerIds = [],
    }: ClipComposerProps,
    ref,
  ) {
    const form = useForm<ClipComposerValues>({
      resolver: zodResolver(clipComposerSchema),
      defaultValues: { ...DEFAULTS, ...defaultValues },
    });
    const isDirty = form.formState.isDirty;

    // Keep the clip WINDOW in sync with the parent without resetting
    // the form. The parent (Coach Review v2) rebuilds `defaultValues`
    // on every `clipWindow` change — a track drag, a preset, or the
    // I/O shortcuts — so a `form.reset()` here would wipe the coach's
    // unsaved title / description / category / visibility / players
    // every time they nudge the IN/OUT handles. Only `start_seconds`
    // / `end_seconds` are pushed in, via `setValue`; the other fields
    // are left untouched. A genuinely DIFFERENT clip (edit target
    // change, drawer re-open) arrives as a fresh mount — the parent
    // key-bumps the composer — so the `useForm` initializer seeds the
    // full field set and no reset-on-prop-change is needed.
    const windowStart = defaultValues?.start_seconds;
    const windowEnd = defaultValues?.end_seconds;
    useEffect(() => {
      if (
        windowStart !== undefined &&
        windowStart !== form.getValues("start_seconds")
      ) {
        form.setValue("start_seconds", windowStart, { shouldDirty: true });
      }
      if (
        windowEnd !== undefined &&
        windowEnd !== form.getValues("end_seconds")
      ) {
        form.setValue("end_seconds", windowEnd, { shouldDirty: true });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowStart, windowEnd]);

    useImperativeHandle(
      ref,
      () => ({
        getValues: () => form.getValues(),
        isDirty: () => isDirty,
        reset: (next?: Partial<ClipComposerValues>) =>
          form.reset({ ...DEFAULTS, ...(next ?? {}) }),
      }),
      [form, isDirty],
    );

    // A4: smart visibility default — flip "team" → "player" when the
    // coach tags the FIRST player on an untouched-visibility clip.
    // Same edge-detection + never-override-touched rules as the note
    // composer (see Composer.tsx).
    const watchedPlayerIds = form.watch("player_ids");
    const prevHadPlayersRef = useRef(false);
    useEffect(() => {
      const hasPlayers = (watchedPlayerIds ?? []).length > 0;
      const wasEmpty = !prevHadPlayersRef.current;
      prevHadPlayersRef.current = hasPlayers;
      if (
        hasPlayers &&
        wasEmpty &&
        !form.formState.dirtyFields.visibility &&
        form.getValues("visibility") === "team"
      ) {
        form.setValue("visibility", "player", { shouldDirty: false });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedPlayerIds]);

    const start = Number(form.watch("start_seconds"));
    const end = Number(form.watch("end_seconds"));
    const visibility = form.watch("visibility");
    const activeVisibility = VISIBILITY_OPTIONS.find(
      (v) => v.value === visibility,
    );

    // Live duration label — red when the window is degenerate or
    // over the 120s cap. Mirrors the legacy composer's live label.
    const duration = useMemo(() => {
      if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return { seconds: NaN, valid: false };
      }
      const d = end - start;
      return {
        seconds: d,
        valid: d > 0 && d <= MAX_CLIP_DURATION_SECONDS,
      };
    }, [start, end]);

    return (
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
        data-testid="coach-review-clip-composer"
      >
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="flex min-w-0 flex-col gap-3">
            <Field label="Title" error={form.formState.errors.title?.message}>
              <input
                type="text"
                {...form.register("title")}
                data-testid="clip-composer-title"
                className="w-full rounded border border-rule bg-canvas px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </Field>

            <Field
              label="Description"
              error={form.formState.errors.description?.message}
            >
              <textarea
                {...form.register("description")}
                data-testid="clip-composer-description"
                rows={8}
                className="w-full rounded border border-rule bg-canvas px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
              />
            </Field>
          </div>

          <aside className="flex min-w-0 flex-col gap-3 rounded border border-rule bg-surface p-3 shadow-sm">
            <div
              className="rounded border border-rule bg-canvas px-3 py-2 text-xs"
              data-testid="clip-composer-match-anchor"
            >
              <span className="text-ink-muted uppercase tracking-wider font-display">
                Match
              </span>
              <div className="text-ink mt-0.5">
                {matchLabel} · {slot}
              </div>
              {isEditing ? (
                <div className="text-ink-muted mt-1">
                  Match + slot are fixed on edit.
                </div>
              ) : null}
            </div>

            {sourceNoteId != null ? (
              <div
                className="self-start rounded border border-accent/40 bg-accent/10 px-2.5 py-1 text-xs text-ink"
                data-testid="clip-composer-source-note"
              >
                From note #{sourceNoteId}
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-2">
              <Field
                label="Start"
                error={form.formState.errors.start_seconds?.message}
              >
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  {...form.register("start_seconds", { valueAsNumber: true })}
                  data-testid="clip-composer-start"
                  className="w-full rounded border border-rule bg-canvas px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </Field>
              <Field
                label="End"
                error={form.formState.errors.end_seconds?.message}
              >
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  {...form.register("end_seconds", { valueAsNumber: true })}
                  data-testid="clip-composer-end"
                  className="w-full rounded border border-rule bg-canvas px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </Field>
            </div>

            <p
              className={`text-xs font-mono ${
                duration.valid ? "text-ink-muted" : "text-destructive"
              }`}
              data-testid="clip-composer-duration"
            >
              Duration:{" "}
              {Number.isFinite(duration.seconds)
                ? `${duration.seconds.toFixed(1)}s`
                : "—"}
              {Number.isFinite(duration.seconds) && duration.seconds > 0
                ? ` (${formatPlayhead(start)} → ${formatPlayhead(end)})`
                : ""}
              {!duration.valid
                ? duration.seconds > MAX_CLIP_DURATION_SECONDS
                  ? ` · over the ${MAX_CLIP_DURATION_SECONDS}s cap`
                  : " · end must be after start"
                : ""}
            </p>

            <div className="grid grid-cols-2 gap-2">
              <Field label="Category">
                <Controller
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <TkticSelect
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      data-testid="clip-composer-category"
                      options={NOTE_CATEGORIES.map((c) => ({
                        value: c,
                        label: NOTE_CATEGORY_LABELS[c],
                      }))}
                    />
                  )}
                />
              </Field>
              <Field label="Visibility" helper={activeVisibility?.description}>
                <Controller
                  control={form.control}
                  name="visibility"
                  render={({ field }) => (
                    <TkticSelect
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      data-testid="clip-composer-visibility"
                      options={VISIBILITY_OPTIONS.map((v) => ({
                        value: v.value,
                        label: v.label,
                      }))}
                    />
                  )}
                />
              </Field>
            </div>

            <Field label="Tag players">
              {/* A4: quick-add chips from this match's tagging
               * history, minus anyone already tagged here. */}
              {(() => {
                const tagged = form.watch("player_ids") ?? [];
                const taggedSet = new Set(tagged);
                const chips = suggestedPlayerIds
                  .filter((id) => !taggedSet.has(id))
                  .map((id) => players.find((p) => p.id === id))
                  .filter((p): p is Player => Boolean(p));
                if (chips.length === 0) return null;
                return (
                  <div
                    className="mb-2 flex flex-wrap items-center gap-1.5"
                    data-testid="clip-composer-suggested-players"
                  >
                    <span className="text-xs text-ink-muted">Suggested:</span>
                    {chips.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() =>
                          form.setValue(
                            "player_ids",
                            [...(form.getValues("player_ids") ?? []), p.id],
                            { shouldDirty: true },
                          )
                        }
                        data-testid={`clip-composer-suggested-player-${p.id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-rule px-2 py-0.5 text-xs text-ink transition-colors hover:bg-field"
                      >
                        <PlusIcon size={11} strokeWidth={2.5} />
                        {p.display_name}
                        {p.jersey_number ? ` · #${p.jersey_number}` : ""}
                      </button>
                    ))}
                  </div>
                );
              })()}
              <TkticMultiSelect
                value={form.watch("player_ids") ?? []}
                onChange={(next) =>
                  form.setValue("player_ids", next, { shouldDirty: true })
                }
                data-testid="clip-composer-player-ids"
                placeholder="No players tagged"
                options={players.map((p) => ({
                  value: p.id,
                  label: `${p.display_name}${p.jersey_number ? ` · #${p.jersey_number}` : ""}`,
                }))}
              />
            </Field>

            <button
              type="submit"
              data-testid="clip-composer-submit"
              disabled={form.formState.isSubmitting}
              className="mt-auto rounded bg-accent px-4 py-2 text-sm font-display uppercase tracking-wide text-accent-ink transition-colors hover:bg-accent-hover disabled:bg-field disabled:text-ink-muted"
            >
              {form.formState.isSubmitting ? "Saving…" : submitLabel}
            </button>
          </aside>
        </div>
      </form>
    );
  },
);

/** Field wrapper — label + optional helper + error. Mirrors the
 * one in `Composer.tsx` (kept local to avoid an export just for
 * this; if a third composer appears, lift it to a shared file). */
function Field(props: {
  label: string;
  helper?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
      </span>
      {props.children}
      {props.helper ? (
        <span className="text-xs text-ink-muted">{props.helper}</span>
      ) : null}
      {props.error ? (
        <span className="text-xs text-destructive">{props.error}</span>
      ) : null}
    </label>
  );
}
